import React from 'react';
import Dropzone from 'react-dropzone';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';

import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Fab from '@mui/material/Fab';
import IconButton from '@mui/material/IconButton';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/DeleteForever';
import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import {Typography} from '@mui/material';
import Link from '@mui/material/Link';
import FormHelperText from '@mui/material/FormHelperText';

import Jimp from 'jimp';

import {commonOperations} from './../../../reducks/common';
import {downloadAttachedFile} from '../../../common/common.js';
import withRouter from '../withRouter.js';
import {withMediaQuery} from '../../atoms/withMediaQuery.js';

/** 写真長辺の最大長 */
const MAX_PHOTO_LENGTH = 1008;

/**
 * @typedef UploadFileInfo アップロードファイル情報
 * @property {string} ContentDocumentId ContentDocumentテーブルのId。S3登録データの場合はキー
 * @property {string} FileName 拡張子付きファイル名。同名ファイルチェックに使用する。
 * @property {string} FileBody dataスキーム付きBase64形式データ。SF未保存のファイルのみ。
 * @property {string} ContentType dataスキームSF未保存のファイルのみ。
 * @property {File} FileData アップロードするファイルの実体。SF未保存のファイルのみ。
 * @property {object} anchor ダウンロードリンク。SF保存済のファイルのみ。
 */

/**
 * ファイルアップロード
 */
class Upload extends React.Component {
  constructor(props) {
    super(props);

    /** @type UploadFileInfo[] */
    this.files = [];

    /** @type UploadFileInfo[] */
    this.deleteFiles = [];
    this.image = null;

    let index = 1;
    for (const row of this.props.initFile) {
      if (Object.hasOwnProperty.call(row, 'ContentType') ||
          Object.hasOwnProperty.call(row, 'anchor')) {
        // ファイル未保存、または保存済かつリンク変換済の場合
        let _row = {...row};
        _row.ContentDocumentId = index++;
        this.files.push(row);
      } else {
        const anchorAttrs = {};
        anchorAttrs['onClick'] = (event) => {
          this.doDownloadAttachedFile(
              row.VersionData,
              row.FileType,
              row.PathOnClient);
        };
        let data = {anchor: null, FileName: '', ContentDocumentId: ''};
        data.FileName = row.PathOnClient;
        data.anchor = anchorAttrs;
        data.ContentDocumentId = row.ContentDocumentId;
        this.files.push(data);
      }
    }

    // 画像ファイルを拡大するフラグが消えてしまうのでstateに保存する
    this.state = {
      dialog: false,
    };
  }

  /**
   * 添付ファイルダウンロード
   * @param {*} url
   * @param {*} fileType
   * @param {*} fileName
   */
  doDownloadAttachedFile = async (url, fileType, fileName) => {
    const {userInfo} = this.props;
    try {
      downloadAttachedFile(
          userInfo, url,
          fileType, fileName);
    } catch (error) {
      this.props.doShowMessage({
        message: {
          id: 'BFY000051E',
          values: ['ファイル', 'ダウンロード'],
        },
      });
    }
  };

  /**
   * 拡張子取得
   * @param {object} file
   * @return {string} 拡張子
   */
  getFileType = (file) => {
    const pos = file.name.lastIndexOf('.');
    let filetype = '';
    if (pos !== -1) filetype = file.name.slice(pos);
    return filetype;
  };

  /**
   * エラーメッセージをまとめて表示
   * @param {string} msgId
   * @param {array<string>} fileNames
   * @param {string} option
   */
  setErr = (msgId, fileNames, option) => {
    // fileNamesは以下の形式にする
    // ['ファイル名1','ファイル名2', ...]
    this.clearFile();

    const messages = [];
    if (fileNames) {
      for (const fileName of fileNames) {
        messages.push({
          id: msgId,
          values: option != null ? [fileName, option] : [fileName],
        });
      }
    } else {
      messages.push({
        id: msgId,
      });
    }

    this.props.doShowMessage({message: messages});
  };

  /**
   * 親コンポーネントへの情報返却
   * @param {object} files 登録ファイルリスト
   * @param {object} deleteFiles 削除ファイルリスト
   */
  returnFile = (files, deleteFiles) => {
    const {componentId, fileSetHandler} = this.props;
    if (fileSetHandler) {
      fileSetHandler(files, deleteFiles, componentId);
    }
  };

  /**
   * 選択ファイル読み込み
   * @param {File} file 選択ファイル
   * @param {string} contentDocumentId ファイルアップロード済の場合、ContentVersionのIdを渡す
   */
  pushFile = async (file, contentDocumentId) => {
    const reader = new FileReader();
    reader.fileName = file.name;

    reader.onload = (e) => {
      const contentType = (e.target.result).split(';')[0].split(':')[1];
      const newFile = {'FileBody': e.target.result,
        'FileName': e.target.fileName,
        'ContentType': contentType,
        'ContentDocumentId': contentDocumentId,
        'FileData': file,
      };
      const createFiles = this.files.concat(newFile);
      this.files = createFiles;
      this.returnFile(this.files, this.deleteFiles);

      // JPEGファイルの場合、サイズを変更し差し替える
      if (file.type == Jimp.MIME_JPEG) {
        this.resizeImage(file).then((resizedFile) => {
          newFile.FileData = resizedFile;
        });
      }
    };
    reader.readAsDataURL(file);
    // input:fileの情報をクリア
    this.clearFile();
  };

  /**
   * 画像のリサイズ
   * @param {File} file JPEGファイル
   */
  resizeImage = async (file) => {
    const buf = Buffer.from(await file.arrayBuffer());

    return new Promise((resolve) => {
      Jimp.read(buf, async (err, image) => {
        // 読み込み時エラー
        if (err) {
          console.info('画像ファイル読み込みエラーのため指定ファイルをそのまま使用');
          console.error(err);
          resolve(file);
        }

        // 現在の画像の幅/高さを取得
        const bmp = image.bitmap;
        const width = bmp.width;
        const height = bmp.height;

        // 長辺の長さが最大長を超える場合は最大長にする
        let changeSize = this.changePhotoSize(width, height);
        let changeWidth = changeSize.width;
        let changeHeight = changeSize.height;
        if (changeWidth != null) {
          image.resize(changeWidth, changeHeight);
        }

        // JPEGのクオリティを80%に設定
        image.quality(80);

        // 新たなFileを生成する
        const type = file.type;
        const buf = await image.getBufferAsync(type);

        try {
          const newFile = new File([buf], file.name, {
            type: type,
            lastModified: file.lastModified,
          });

          resolve(newFile);
        } catch (err) {
          // 変換エラー
          console.info('画像ファイル生成エラーのため指定ファイルをそのまま使用');
          console.error(err);
          resolve(file);
        }
      });
    });
  };

  /**
   * 写真サイズ変更
   * @param {object} width
   * @param {object} height
   * @return {object} サイズ
   */
  changePhotoSize = (width, height) => {
    let changeWidth = null;
    let changeHeight = null;
    if (width > height) {
      if (width > MAX_PHOTO_LENGTH) {
        changeWidth = MAX_PHOTO_LENGTH;
        changeHeight = Jimp.AUTO;
      }
    }
    if (width <= height) {
      if (height > MAX_PHOTO_LENGTH) {
        changeHeight = MAX_PHOTO_LENGTH;
        changeWidth = Jimp.AUTO;
      }
    }
    return {width: changeWidth, height: changeHeight};
  };

  /**
   * 選択ファイルの選択解除
   * @param {object} e
   */
  delFile = (e) => {
    const files = this.files;
    const delFiles = this.deleteFiles;
    const createFiles = [];
    files.forEach((file) => {
      if (file !== e) createFiles.push(file);
      if (file === e && e.ContentDocumentId) {
        delFiles.push(e);
      }
    });
    this.files = createFiles;
    this.deleteFiles = delFiles;
    this.returnFile(this.files, this.deleteFiles);
  };

  /**
   * input:fileの情報をクリア
   * @param {object} e
   */
  clearFile = (e) => {
    document.getElementById('btn_addfile_' + this.props.componentId).value = '';
  };

  /**
   * イメージデータのプレビュー
   * @param {object} e
   */
  viewFile = (e) => {
    this.image = e;
    this.setState({dialog: true});
  };

  /**
   * ダイアログのクローズ
   * @param {object} e
   */
  handleClose = (e) => {
    this.image = null;
    this.setState({dialog: false});
  };

  /**
   * ファイルドロップ時の処理
   * @param {array} acceptedFiles
   */
  onDrop = async (acceptedFiles) => {
    const files = acceptedFiles;

    // 添付ファイル数が最大ファイル数を超える場合、エラー
    if ((files.length + this.files.length) >
            this.props.maxFileCount) {
      this.setErr('BFY000057E', null);
      return;
    }

    // カスタムチェック処理
    const error = this.customCheck(files);
    if (error != null) {
      this.setErr(error.id, error.values);
      return;
    }

    const sizeErrFiles = [];
    for (const file of files) {
      if (file.size > this.props.maxFileSize) {
        sizeErrFiles.push(file.name);
      } else {
        await this.pushFile(file);
      }
    }
    if (sizeErrFiles.length > 0) {
      const maxSize = this.props.maxFileSize ?
       Math.floor(this.props.maxFileSize / 1000000) : 0;
      this.setErr('BFY000028E', sizeErrFiles, maxSize);
    }
  };

  /**
   * カスタムチェック
   * @param {*} files
   * @return {object} エラー
   */
  customCheck = (files) => {
    const {customChecks} = this.props;
    let result = null;
    if (customChecks == null) {
      return result;
    }
    for (const check of customChecks) {
      const error = check(files, this.files);
      if (error != null) {
        result = error;
        break;
      }
    }
    return result;
  };

  /**
   * DropZoneに渡すMINE TYPEを作成
   * @param {string} acceptFileType
   * @return {object} MINE TYPE
  */
  createAcceptedFileType = (acceptFileType) => {
    let acceptObj = {};
    if (!acceptFileType || acceptFileType.length <= 0) {
      return acceptObj;
    }
    for (const fileExtension of acceptFileType.split(',')) {
      let mineType = this.selectMineType(fileExtension);
      if (mineType == 'image/jpeg') {
        if (Object.hasOwnProperty.call(acceptObj, 'image/jpeg')) {
          acceptObj[mineType].push(fileExtension);
        } else {
          acceptObj[mineType] = [fileExtension];
        }
      } else {
        acceptObj[mineType] = [fileExtension];
      }
    }
    return acceptObj;
  };

  /**
   * mineType取得
   * @param {string} fileExtension 拡張子
   * @return {string} minetype
   */
  selectMineType = (fileExtension) => {
    let mineType = '';
    if (fileExtension == '.pdf') {
      mineType = 'application/pdf';
    }
    if (fileExtension == '.doc') {
      mineType = 'application/msword';
    }
    if (fileExtension == '.docx') {
      mineType = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
    }
    if (fileExtension == '.xls') {
      mineType = 'application/vnd.ms-excel';
    }
    if (fileExtension == '.xlsx') {
      mineType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    }
    if (fileExtension == '.jpg' || fileExtension == '.jpeg') {
      mineType = 'image/jpeg';
    }
    return mineType;
  };

  /**
   * 画面描画
   * @param {bool} _disabled disabled
   * @return {object} オブジェクト
   */
  viewDropzone = (_disabled) => {
    if (this.props.disabled) {
      return <></>;
    }
    return (
      <Box style={{padding: '5px', width: '100%', borderStyle: 'dashed'}} border={1} borderColor="grey.500" borderRadius="8px">
        <Dropzone
          onDropRejected={(fileRejections) => {
            const files = fileRejections;
            const fileNames = [];
            for (const obj of files) {
              const file = obj.file;
              if (file.name != '') {
                fileNames.push(file.name);
              }
            }
            if (fileNames.length > 0) {
              this.setErr('BFY000056E', fileNames);
            }
          }}
          onDrop={this.onDrop}
          accept={this.createAcceptedFileType(this.props.acceptFileType)}
          disabled={_disabled}
        >
          {({getRootProps, getInputProps}) => (
            <section>
              <div {...getRootProps()}>
                <Box display="flex" alignItems="center">
                  <input {...getInputProps()} id={'btn_addfile_' + this.props.componentId} />
                  <Fab id="add" color="primary" aria-label="add" size="medium" disabled={_disabled} style={{zIndex: 1}}>
                    <AddIcon />
                  </Fab>
                  <Typography align="center" variant="caption" display="block" style={{width: '80%'}} gutterBottom>
                  ここにファイルをドラッグアンドドロップするか、ここをクリックしてファイルを選択してください。
                  </Typography>
                </Box>
              </div>
            </section>
          )}
        </Dropzone>
      </Box>
    );
  };

  render() {
    const {required} = this.props;
    const isImage = this.files.filter((file) =>
      Object.hasOwnProperty.call(file, 'ContentType') &&
      (file.ContentType).indexOf('image') != -1).length > 0;
    if (!isImage) {
      this.files = [];
    }
    if (this.files.length == 0) {
      let index = 1;
      for (const row of this.props.initFile) {
        if (Object.hasOwnProperty.call(row, 'ContentType') ||
            Object.hasOwnProperty.call(row, 'anchor')) {
          // ファイル未保存、または保存済かつリンク変換済の場合
          let _row = {...row};
          _row.ContentDocumentId = index++;
          this.files.push(row);
        } else {
          const anchorAttrs = {};
          anchorAttrs['onClick'] = (event) => {
            this.doDownloadAttachedFile(
                row.VersionData,
                row.FileType,
                row.PathOnClient);
          };
          let data = {anchor: null, FileName: '', ContentDocumentId: ''};
          data.FileName = row.PathOnClient;
          data.anchor = anchorAttrs;
          data.ContentDocumentId = row.ContentDocumentId;
          this.files.push(data);
        }
      }
    }

    const files = this.files;
    const _disabled = this.props.disabled ||
      (files.length >= this.props.maxFileCount); ;
    const preview = files.map((file) => {
      const isImageFile = Object.hasOwnProperty.call(file, 'ContentType') &&
                          ((file.ContentType).indexOf('image') != -1);
      return (
        <React.Fragment key={file.ContentDocumentId}>
          <table width="100%" border="0" cellPadding="0" cellSpacing="0">
            <tbody>
              {this.props.previewFlag && isImageFile &&
              <tr>
                <td width={this.props.previewWidth}>
                  <img width="auto" height="300px" src={file.FileBody} alt={file.FileName}
                    style={{cursor: 'pointer'}}
                    onMouseDown={()=>{
                      this.viewFile(file);
                    }}/>
                </td>
                <td width="65%">&nbsp;</td>
              </tr>
              }
              <tr>
                <td colSpan="2">
                  <IconButton color="primary" aria-label="delete" size="medium" disabled={this.props.disabled}
                    onClick={()=>{
                      this.delFile(file);
                    }}>
                    <DeleteIcon />
                  </IconButton>
                  {isImageFile &&
                  file.FileName
                  }
                  {!isImageFile &&
                  <Link
                    href={file.FileBody}
                    download={file.FileName}
                    target="_blank"
                    rel="noopener"
                    underline="hover">
                    {file.FileName}
                  </Link>
                  }
                  {!Object.hasOwnProperty.call(file, 'ContentType') &&
                   file.ContentDocumentId &&
                   <button type="button" className='bf-buttonLink' key={file.FileName} {...file.anchor}>
                     {file.FileName}
                   </button>
                  }
                </td>
              </tr>
            </tbody>
          </table>
        </React.Fragment>
      );
    });
    return (
      <div>
        {!this.props.disabled &&
        required === true && files.length == 0 &&
          <FormHelperText error>{'必須入力です'}</FormHelperText>
        }
        {preview}
        <div>
          {this.viewDropzone(_disabled)}
        </div>

        <Dialog
          open={this.state.dialog}
          onClose={this.handleClose}
          fullWidth={true}
          maxWidth="xl"
          scroll="paper"
          aria-labelledby="scroll-dialog-title"
        >
          {(this.image) &&
            <React.Fragment>
              <DialogTitle id="scroll-dialog-title">
                {this.image.FileName}
                <IconButton
                  style={{position: 'absolute', right: '0px', top: '0px', color: '#a5a5a5'}}
                  onClick={this.handleClose} size="large">
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              <DialogContent>
                <DialogContentText>
                  <img width="100%" src={this.image.FileBody} alt={this.image.FileName} />
                </DialogContentText>
              </DialogContent>
            </React.Fragment>
          }
        </Dialog>
      </div>
    );
  }
}


Upload.propTypes = {
  classes: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  componentId: PropTypes.string,
  customChecks: PropTypes.array,        // カスタムチェックメソッド一覧
  maxFileCount: PropTypes.number,       // 1度にアップロード可能なファイル数
  maxFileSize: PropTypes.number,        // 1ファイルあたりの許容サイズ
  previewFlag: PropTypes.bool,          // イメージファイルのプレビュー機能有無
  previewWidth: PropTypes.string,       // プレビューイメージの表示幅
  acceptFileType: PropTypes.string,     // 許可する拡張子を「.（ドット）」込みでカンマ区切りで指定 ex）.jpg,.pdf
  fileSetHandler: PropTypes.func,        // 選択ファイルを格納するための関数
  initFile: PropTypes.array,
  required: PropTypes.bool,
  input: PropTypes.shape({
    onChange: PropTypes.func.isRequired,
  }),
};

Upload.defaultProps = {
  componentId: 'default',
  maxFileCount: 3,
  maxFileSize: 1000000,
  previewFlag: false,
  acceptFileType: '.txt',
  initFile: [],
  disabled: false,
};

Upload.propTypes = {
  disabled: PropTypes.bool,
  userInfo: PropTypes.object,
  doShowMessage: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  userInfo: state.auth.userInfo,
});

const mapDispatchToProps = {
  doShowMessage: commonOperations.doShowMessage,
};

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(withRouter(withMediaQuery()(Upload)));
